import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Gift, Star, Zap, Coffee, ShoppingBag,
  CreditCard, Ticket, PartyPopper, Check, Lock, TrendingUp,
} from 'lucide-react';

// ─── Mock Data ──────────────────────────────────────────────────────

interface Reward {
  id: string;
  name: string;
  description: string;
  pointsCost: number;
  category: 'cash' | 'food' | 'experience' | 'merch';
  icon: React.ComponentType<{ className?: string }>;
  popular?: boolean;
  limited?: boolean;
}

const REWARDS: Reward[] = [
  { id: '1', name: '$25 Cash Back', description: 'Direct deposit to your bank account', pointsCost: 500, category: 'cash', icon: CreditCard, popular: true },
  { id: '2', name: '$50 Cash Back', description: 'Direct deposit to your bank account', pointsCost: 950, category: 'cash', icon: CreditCard },
  { id: '3', name: '$100 Cash Back', description: 'Best value — 5% bonus points', pointsCost: 1800, category: 'cash', icon: CreditCard },
  { id: '4', name: 'Free Shift Meal', description: 'Any entree + drink on your next shift', pointsCost: 150, category: 'food', icon: Coffee, popular: true },
  { id: '5', name: 'Family Dinner for 4', description: 'Full dinner experience for your family', pointsCost: 800, category: 'food', icon: Coffee },
  { id: '6', name: '$50 DoorDash Credit', description: 'When you don\'t want to cook after work', pointsCost: 1000, category: 'food', icon: ShoppingBag },
  { id: '7', name: 'Priority Scheduling', description: 'First pick of shifts for 2 weeks', pointsCost: 600, category: 'experience', icon: Star },
  { id: '8', name: 'Extra Day Off', description: 'Paid day off — you earned it', pointsCost: 2000, category: 'experience', icon: PartyPopper, limited: true },
  { id: '9', name: 'Bespoke Champion Tee', description: 'Limited edition — flex on \'em', pointsCost: 300, category: 'merch', icon: Ticket },
];

interface RewardHistory {
  date: string;
  reward: string;
  points: number;
}

const HISTORY: RewardHistory[] = [
  { date: 'Mar 10', reward: '$25 Cash Back', points: 500 },
  { date: 'Mar 3', reward: 'Free Shift Meal', points: 150 },
  { date: 'Feb 24', reward: '$50 Cash Back', points: 950 },
  { date: 'Feb 15', reward: 'Bespoke Champion Tee', points: 300 },
];

const POINTS_BALANCE = 2340;
const POINTS_EARNED_THIS_MONTH = 890;
const POINTS_LIFETIME = 8240;

// ─── Component ──────────────────────────────────────────────────────

const RewardsStore: React.FC = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [claimedId, setClaimedId] = useState<string | null>(null);

  const categories = [
    { key: 'all', label: 'All Rewards' },
    { key: 'cash', label: 'Cash Back' },
    { key: 'food', label: 'Food & Drink' },
    { key: 'experience', label: 'Experiences' },
    { key: 'merch', label: 'Merch' },
  ];

  const filteredRewards = selectedCategory === 'all'
    ? REWARDS
    : REWARDS.filter(r => r.category === selectedCategory);

  const handleClaim = (reward: Reward) => {
    if (reward.pointsCost > POINTS_BALANCE) return;
    setClaimedId(reward.id);
    setTimeout(() => setClaimedId(null), 2000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button onClick={() => navigate('/restaurant/mockups')} className="flex items-center gap-1.5 text-purple-200 hover:text-white text-sm mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Hub
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Rewards Store</h1>
              <p className="text-purple-200 text-sm">Turn great service into real rewards</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-white">{POINTS_BALANCE.toLocaleString()}</div>
              <div className="text-purple-200 text-xs">points available</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Points summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 -mt-4">
          {[
            { label: 'Available Points', value: POINTS_BALANCE.toLocaleString(), sub: 'Ready to spend', color: 'purple' },
            { label: 'Earned This Month', value: POINTS_EARNED_THIS_MONTH.toLocaleString(), sub: '+12% vs last month', color: 'emerald' },
            { label: 'Lifetime Points', value: POINTS_LIFETIME.toLocaleString(), sub: 'Since you joined', color: 'indigo' },
          ].map((stat, i) => (
            <div key={i} className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
              <div className="text-xs text-gray-500 mb-1">{stat.label}</div>
              <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
              <div className={`text-xs ${stat.color === 'emerald' ? 'text-emerald-600' : 'text-gray-400'}`}>{stat.sub}</div>
            </div>
          ))}
        </div>

        {/* How it works */}
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200 p-5">
          <h3 className="text-sm font-semibold text-purple-800 mb-3">How You Earn Points</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { action: 'Successful upsell', pts: '+10 pts', icon: Zap },
              { action: 'Drink promotion', pts: '+5 pts', icon: Coffee },
              { action: 'Perfect greeting', pts: '+3 pts', icon: Star },
              { action: 'Daily streak bonus', pts: '+25 pts', icon: TrendingUp },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 bg-white/60 rounded-lg px-3 py-2">
                <item.icon className="w-4 h-4 text-purple-500 flex-shrink-0" />
                <div>
                  <div className="text-xs font-medium text-purple-800">{item.action}</div>
                  <div className="text-xs text-purple-600 font-bold">{item.pts}</div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-purple-600 mt-3">
            Points are funded by your restaurant's upsell revenue. When you sell more, everyone wins.
            <span className="font-medium"> Your manager has committed 3% of upsell revenue to the rewards pool.</span>
          </p>
        </div>

        {/* Category filter */}
        <div className="flex items-center gap-1 flex-wrap">
          {categories.map((cat) => (
            <button
              key={cat.key}
              onClick={() => setSelectedCategory(cat.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                selectedCategory === cat.key
                  ? 'bg-purple-100 text-purple-700 border border-purple-200'
                  : 'text-gray-500 hover:text-gray-700 border border-transparent'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Rewards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredRewards.map((reward) => {
            const canAfford = reward.pointsCost <= POINTS_BALANCE;
            const justClaimed = claimedId === reward.id;
            return (
              <div
                key={reward.id}
                className={`bg-white rounded-xl border shadow-sm p-5 transition-all duration-200 ${
                  justClaimed ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 hover:shadow-md'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    canAfford ? 'bg-purple-100' : 'bg-gray-100'
                  }`}>
                    <reward.icon className={`w-5 h-5 ${canAfford ? 'text-purple-600' : 'text-gray-400'}`} />
                  </div>
                  <div className="flex items-center gap-1.5">
                    {reward.popular && (
                      <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full uppercase">Popular</span>
                    )}
                    {reward.limited && (
                      <span className="text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded-full uppercase">Limited</span>
                    )}
                  </div>
                </div>
                <h3 className={`text-sm font-semibold mb-0.5 ${canAfford ? 'text-gray-900' : 'text-gray-400'}`}>
                  {reward.name}
                </h3>
                <p className="text-xs text-gray-500 mb-4">{reward.description}</p>
                <div className="flex items-center justify-between">
                  <div className={`text-sm font-bold ${canAfford ? 'text-purple-600' : 'text-gray-400'}`}>
                    {reward.pointsCost.toLocaleString()} pts
                  </div>
                  <button
                    onClick={() => handleClaim(reward)}
                    disabled={!canAfford || justClaimed}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                      justClaimed
                        ? 'bg-emerald-100 text-emerald-700'
                        : canAfford
                          ? 'bg-purple-600 text-white hover:bg-purple-700 active:scale-95'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {justClaimed ? (
                      <span className="flex items-center gap-1"><Check className="w-3 h-3" /> Claimed!</span>
                    ) : canAfford ? (
                      'Redeem'
                    ) : (
                      <span className="flex items-center gap-1"><Lock className="w-3 h-3" /> Need more</span>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Recent redemptions */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Recent Redemptions</h3>
          <div className="space-y-2">
            {HISTORY.map((h, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center">
                    <Gift className="w-4 h-4 text-purple-400" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-900">{h.reward}</div>
                    <div className="text-xs text-gray-400">{h.date}</div>
                  </div>
                </div>
                <div className="text-sm font-medium text-gray-500">-{h.points} pts</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RewardsStore;
